import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { UserCheck, FileText, BarChart3 } from 'lucide-react';
interface SlideComparisonProps {
  onContinue: () => void;
}
interface OptimizationBucket {
  icon: React.ReactNode;
  label: string;
  todayScore: number;
  snakkerScore: number;
  todayDesc: string;
  snakkerDesc: string;
  maxScore: number;
}
function ScoreBar({
  score,
  maxScore,
  color,
  animated
}: {
  score: number;
  maxScore: number;
  color: 'red' | 'teal';
  animated: boolean;
}) {
  const percentage = score / maxScore * 100;
  return <div className="flex items-center gap-3 w-full" data-id="element-92">
      <div className="flex-1 h-8 bg-navy-700/60 rounded-lg overflow-hidden relative" data-id="element-93">
        <motion.div initial={{
        width: 0
      }} animate={{
        width: animated ? `${percentage}%` : 0
      }} transition={{
        duration: 1,
        ease: 'easeOut',
        delay: 0.3
      }} className={`h-full rounded-lg ${color === 'teal' ? 'bg-gradient-to-r from-teal-500 to-teal-400 shadow-[0_0_12px_rgba(0,212,170,0.3)]' : 'bg-gradient-to-r from-red-500/40 to-red-400/50'}`} data-id="element-94" />
        {/* Score dots */}
        <div className="absolute inset-0 flex items-center px-2 gap-1.5" data-id="element-95">
          {Array.from({
          length: maxScore
        }).map((_, i) => <motion.div key={i} initial={{
          opacity: 0,
          scale: 0
        }} animate={{
          opacity: animated ? 1 : 0,
          scale: animated ? 1 : 0
        }} transition={{
          delay: 0.5 + i * 0.15
        }} className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${i < score ? color === 'teal' ? 'border-white/80 bg-white/20' : 'border-white/50 bg-white/10' : 'border-white/15 bg-transparent'}`} data-id="element-96">
              {i < score && <div className={`w-2 h-2 rounded-full ${color === 'teal' ? 'bg-white' : 'bg-white/60'}`} data-id="element-97" />}
            </motion.div>)}
        </div>
      </div>
      <motion.span initial={{
      opacity: 0
    }} animate={{
      opacity: animated ? 1 : 0
    }} transition={{
      delay: 1
    }} className={`text-lg font-bold w-12 text-right ${color === 'teal' ? 'text-teal-400' : 'text-red-400/70'}`} data-id="element-98">
        {score}/{maxScore}
      </motion.span>
    </div>;
}
export function SlideComparison({
  onContinue
}: SlideComparisonProps) {
  const [isFuture, setIsFuture] = useState(false);
  const buckets: OptimizationBucket[] = [{
    icon: <UserCheck className="w-5 h-5" data-id="element-99" />,
    label: 'Personalization',
    todayScore: 1,
    snakkerScore: 3,
    todayDesc: 'Same journey for all HCPs, name merge fields only',
    snakkerDesc: 'Individualized journeys, adaptive content paths',
    maxScore: 5
  }, {
    icon: <FileText className="w-5 h-5" data-id="element-100" />,
    label: 'Content',
    todayScore: 1,
    snakkerScore: 4,
    todayDesc: 'One-size-fits-all PDFs, no interaction or gamification',
    snakkerDesc: 'Interactive quizzes, gamification, peer benchmarks, continuously iterated',
    maxScore: 5
  }, {
    icon: <BarChart3 className="w-5 h-5" data-id="element-101" />,
    label: 'Actionable Data',
    todayScore: 0,
    snakkerScore: 3,
    todayDesc: 'Open rates, CTR — vanity metrics with no behavioral layer',
    snakkerDesc: 'All quantitative metrics plus behavioral insights, motivations & knowledge gaps',
    maxScore: 5
  }];
  return <div className="h-full w-full flex flex-col px-12 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-102">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-4xl md:text-5xl font-serif leading-tight text-white text-center mb-10" data-id="element-103">
        What if legacy brands were
        <br data-id="element-104" />
        <span className="text-teal-400" data-id="element-105">continuously optimized?</span>
      </motion.h2>

      <div className="flex-1 flex flex-col items-center" data-id="element-106">
        {/* Toggle */}
        <div className="flex bg-navy-900 p-1.5 rounded-full mb-10 border border-navy-600 relative" data-id="element-107">
          <motion.div className="absolute top-1.5 bottom-1.5 w-[calc(50%-6px)] bg-teal-400 rounded-full shadow-lg" animate={{
          left: isFuture ? 'calc(50% + 3px)' : '6px'
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} data-id="element-108" />
          <button onClick={() => setIsFuture(false)} className={`relative z-10 px-8 py-2.5 rounded-full font-medium transition-colors ${!isFuture ? 'text-navy-900' : 'text-white/80 hover:text-white'}`} data-id="element-109">
            Today
          </button>
          <button onClick={() => setIsFuture(true)} className={`relative z-10 px-8 py-2.5 rounded-full font-medium transition-colors ${isFuture ? 'text-navy-900' : 'text-white/80 hover:text-white'}`} data-id="element-110">
            Snakker Model
          </button>
        </div>

        {/* Score Cards */}
        <div className="w-full max-w-4xl space-y-6 flex-1 flex flex-col justify-center" data-id="element-111">
          <AnimatePresence mode="wait" data-id="element-112">
            <motion.div key={isFuture ? 'future' : 'today'} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3
          }} className="space-y-5" data-id="element-113">
              {buckets.map((bucket, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: idx * 0.1
            }} className={`rounded-2xl p-6 border ${isFuture ? 'bg-teal-400/5 border-teal-400/20' : 'bg-navy-700/40 border-navy-600'}`} data-id="element-114">
                  <div className="flex items-center gap-3 mb-3" data-id="element-115">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${isFuture ? 'bg-teal-400/15 text-teal-400' : 'bg-navy-600 text-white/50'}`} data-id="element-116">
                      {bucket.icon}
                    </div>
                    <span className={`font-semibold text-base ${isFuture ? 'text-teal-300' : 'text-white/80'}`} data-id="element-117">
                      {bucket.label}
                    </span>
                  </div>

                  <ScoreBar score={isFuture ? bucket.snakkerScore : bucket.todayScore} maxScore={bucket.maxScore} color={isFuture ? 'teal' : 'red'} animated={true} data-id="element-118" />

                  <p className={`mt-3 text-sm leading-relaxed ${isFuture ? 'text-white/80' : 'text-white/50'}`} data-id="element-119">
                    {isFuture ? bucket.snakkerDesc : bucket.todayDesc}
                  </p>
                </motion.div>)}
            </motion.div>
          </AnimatePresence>
        </div>

        <motion.div className="mt-6 h-12" animate={{
        opacity: isFuture ? 1 : 0,
        y: isFuture ? 0 : 10
      }} data-id="element-120">
          <Button onClick={onContinue} className={isFuture ? 'pointer-events-auto' : 'pointer-events-none'} data-id="element-121">
            How it works
          </Button>
        </motion.div>
      </div>
    </div>;
}